import { Chart } from 'chart.js'

import {
  TDatasetMarker,
  TMarkersMap,
  TDrawMarkersPluginOptions
} from 'components_new/molecules/charts/chartjs-plugins'

const clamp = (n: number, min: number, max: number) => {
  return Math.max(min, Math.min(max, n))
}

export const drawPin = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number, // tip (on the line)
  size: number, // width/height of the square part
  radius: number, // corner radius
  pointerH: number, // pointer height
  pointerW: number, // pointer width at the base (where it meets the square)
  fillStyle: string,
  strokeStyle: string,
  lineWidth: number
) => {
  const half = size / 2

  // square part bounding box (above the tip)
  const bottom = y - pointerH // where square meets pointer
  const top = bottom - size
  const left = x - half
  const right = x + half

  const r = clamp(radius, 0, half)

  // pointer base centered on x at the bottom of the square
  const pHalf = pointerW / 2
  const pLeft = x - pHalf
  const pRight = x + pHalf

  ctx.beginPath()

  // start at bottom edge, just right of left corner
  ctx.moveTo(left + r, bottom)

  // bottom edge to pointer base left
  ctx.lineTo(pLeft, bottom)

  // pointer down to tip (x, y) and back up
  ctx.lineTo(x, y)
  ctx.lineTo(pRight, bottom)

  // continue bottom edge to before bottom-right corner
  ctx.lineTo(right - r, bottom)

  // bottom-right corner
  ctx.quadraticCurveTo(right, bottom, right, bottom - r)

  // right edge
  ctx.lineTo(right, top + r)

  // top-right corner
  ctx.quadraticCurveTo(right, top, right - r, top)

  // top edge
  ctx.lineTo(left + r, top)

  // top-left corner
  ctx.quadraticCurveTo(left, top, left, top + r)

  // left edge
  ctx.lineTo(left, bottom - r)

  // bottom-left corner back to start
  ctx.quadraticCurveTo(left, bottom, left + r, bottom)

  ctx.closePath()

  ctx.fillStyle = fillStyle
  ctx.fill()

  ctx.lineWidth = lineWidth
  ctx.strokeStyle = strokeStyle
  ctx.stroke()

  // Return center of the square part for text placement
  return { cx: x, cy: top + size / 2 }
}

const getPinRect = (
  x: number,
  yTip: number, // (x,y) on the line (triangle tip)
  size: number, // square size
  pointerH: number, // pointer height
  padding = 0 // extra px for easier hover
) => {
  const half = size / 2
  const bottom = yTip - pointerH
  const top = bottom - size

  return {
    left: x - half - padding,
    right: x + half + padding,
    top: top - padding,
    bottom: bottom + padding
  }
}

const pointInPinRect = (
  px: number,
  py: number,
  r: ReturnType<typeof getPinRect>
) => {
  return px >= r.left && px <= r.right && py >= r.top && py <= r.bottom
}

const num = (v: unknown, fb: number) => (typeof v === 'number' ? v : fb)

const PIN_GAP = 2
const STACK_STEP = 3
const MAX_STACK_DEPTH = 2

export type TMarkerGroup = {
  datasetIndex: number
  hovered: boolean
  key: string
  markers: TDatasetMarker[]
  // radius the group's pins are laid out with
  r: number
  // pin tip, raised stemHeight above the line
  tipY: number
  x: number
  // where the marker meets the dataset's line or bar
  yLine: number
}

export const getMarkerRadius = (
  m: Pick<TDatasetMarker, 'radius'>,
  opt: TDrawMarkersPluginOptions
) => num(m.radius, num(opt.radius, 4))

export const getPinPointerHeight = (r: number) => Math.max(4, r * 0.7)

/**
 * Room around the chart area for raised pins with radius r, so a pin over
 * the highest point or at either end is never cut off.
 */
export const getMarkerLayoutPadding = (r: number, stemHeight: number) => ({
  left: r,
  right: r + MAX_STACK_DEPTH * STACK_STEP,
  top:
    stemHeight + r * 2 + getPinPointerHeight(r) + MAX_STACK_DEPTH * STACK_STEP
})

export const getChartMarkers = (chart: Chart): TDatasetMarker[] =>
  chart.data.datasets.flatMap((ds, datasetIndex) => {
    const markers = (ds as any).markers as TMarkersMap | undefined

    if (!markers || !chart.isDatasetVisible(datasetIndex)) return []

    return Object.values(markers).flat()
  })

type TPlacedMarker = {
  m: TDatasetMarker
  x: number
  y: number
}

/**
 * Markers grouped by where they are drawn. Markers closer to each other than
 * a pin's width share a group: one pin with the others stacked behind it.
 */
export const getMarkerGroups = (
  chart: Chart,
  opt: TDrawMarkersPluginOptions,
  hoveredKey: string | null
): TMarkerGroup[] => {
  const groups: TMarkerGroup[] = []
  const stemHeight = num(opt.stemHeight, 0)

  chart.data.datasets.forEach((ds, datasetIndex) => {
    if (!chart.isDatasetVisible(datasetIndex)) return

    const markers = (ds as any).markers as TMarkersMap | undefined
    const meta = chart.getDatasetMeta(datasetIndex)

    if (!markers || (meta.type !== 'line' && meta.type !== 'bar')) return

    const placed: TPlacedMarker[] = []

    for (const k in markers) {
      const xIndex = +k
      const entry = markers[xIndex]
      const pt = meta.data[xIndex]

      if (!entry || !pt) continue

      const { x, y } = pt.getProps(['x', 'y'], true)

      if (!Number.isFinite(x) || !Number.isFinite(y)) continue

      for (const m of Array.isArray(entry) ? entry : [entry]) {
        placed.push({ m, x, y })
      }
    }

    const clusters: TPlacedMarker[][] = []

    placed
      .sort((a, b) => a.x - b.x)
      .forEach((p) => {
        const last = clusters[clusters.length - 1]
        const width = getMarkerRadius(p.m, opt) * 2 + PIN_GAP

        if (last && p.x - last[0].x < width) {
          last.push(p)
        } else {
          clusters.push([p])
        }
      })

    clusters.forEach((cluster) => {
      const groupMarkers = cluster.map((p) => p.m)
      const key = `${datasetIndex}:${groupMarkers.map((m) => m.id).join(',')}`
      // the group stands on its first marker's point
      const { x, y: yLine } = cluster[0]

      const hovered =
        key === hoveredKey ||
        groupMarkers.some((m) => m.id === opt.focusedMarkerId)
      const r = Math.max(...groupMarkers.map((m) => getMarkerRadius(m, opt)))

      groups.push({
        datasetIndex,
        hovered,
        key,
        markers: groupMarkers,
        r,
        tipY: yLine - stemHeight,
        x,
        yLine
      })
    })
  })

  return groups
}

// pins behind the front one, peeking out like a stack of cards
export const getStackDepth = (group: TMarkerGroup) =>
  Math.min(group.markers.length - 1, MAX_STACK_DEPTH)

// layer 0 is the front pin; deeper layers sit up and to the right
export const getPinPosition = (group: TMarkerGroup, layer: number) => ({
  tipY: group.tipY - layer * STACK_STEP,
  x: group.x + layer * STACK_STEP
})

export function findHitPinSquare(
  chart: Chart,
  ex: number,
  ey: number,
  opt: TDrawMarkersPluginOptions,
  hoveredKey: string | null
): TMarkerGroup | null {
  const hoverPad = 1

  for (const group of getMarkerGroups(chart, opt, hoveredKey)) {
    for (let layer = 0; layer <= getStackDepth(group); layer++) {
      const { tipY, x } = getPinPosition(group, layer)
      const rect = getPinRect(
        x,
        tipY,
        group.r * 2,
        getPinPointerHeight(group.r),
        hoverPad
      )

      if (pointInPinRect(ex, ey, rect)) return group
    }
  }

  return null
}
